import {
  HowToHandleItRecord,
  SuggestedEvidenceRecord,
} from '@trustero/trustero-api-web/lib/recommend/recommend_pb'
import {
  FRAMEWORK_MODEL_IDS,
  FRAMEWORK_MODEL_ID_TO_NAME,
} from '../../../Utils/globalEnums'

type CustomFlagOnSuggestion = {
  [key: string]: boolean | never | string
}

export type SuggestionsObject = {
  [key: string]: string
} & CustomFlagOnSuggestion

type HowToHandleItObject = {
  frameworkName: string
  howToHandleIt: string
}

/**
 * This function takes in an object of suggestions and returns a new object with the duplicates removed and added to a new key called duplicates
 * @param suggestions - An object of suggestions
 * @returns suggestions: { ...suggestions, duplicates } - suggestions is an object of suggestions with the duplicates removed and duplicates is a string of the duplicates
 */
const dedupeSuggestions = (
  suggestions: SuggestionsObject,
): SuggestionsObject => {
  const duplicates = new Set<string>()
  const dedupedSuggestions: SuggestionsObject = {}
  const newSuggestions: SuggestionsObject = {
    ...suggestions,
  }

  // Loop over our suggestions object
  for (const key in suggestions) {
    // get the suggestion value
    const value = suggestions[key]
    // if the value is a key in our deduped suggestions object
    if (value in dedupedSuggestions) {
      // add the matching value to the duplicates set
      duplicates.add(value)
      // delete the matching value from the new suggestions object at the current key
      delete newSuggestions[key]
      // if the framework for the matched value is still a key in the new suggestions object, delete it
      newSuggestions[dedupedSuggestions[value]] &&
        delete newSuggestions[dedupedSuggestions[value]]
    } else {
      // otherwise, add the current value as a key to the deduped suggestions object with the framework model id as its value
      dedupedSuggestions[value] = key
    }
  }

  if (duplicates.size) {
    newSuggestions.duplicates = Array.from(duplicates).join('\n')
  }
  return newSuggestions
}

/**
 * This function takes in an object of suggestions and returns a markdown string where the keys are the headers and the values are the body. If the key is duplicates, it will be the body and the header will be empty
 * @param suggestions - An object of suggestions
 * @returns markdown - A markdown string
 */
const convertSuggestionsToMarkdown = (
  suggestions: SuggestionsObject,
): string => {
  let markdown = ''
  suggestions.duplicates && (markdown += `<br>\n${suggestions.duplicates}`)

  for (const key in suggestions) {
    if (key !== 'duplicates') {
      markdown.length
        ? (markdown += `<br><br>\n### ${key}\n\n${suggestions[key]}`)
        : (markdown += `<br>\n### ${key}\n\n${suggestions[key]}`)
    }
  }
  return markdown
}

export const getCustomSuggestionText = (
  suggestions: SuggestionsObject,
): string => Object.values(suggestions)[0]

export const convertSuggestionsToText = (
  suggestions: SuggestionsObject,
): string => {
  let text = suggestions['duplicates'] || ''

  for (const key in suggestions) {
    if (key !== 'duplicates') {
      text += suggestions[key]
      text += '\n'
    }
  }

  return text
}

/**
 * This function takes in an array of suggestions and returns an object of suggestions
 * @param suggestedArray - An array of suggestions
 * @returns suggestionsObject - An object of suggestions
 */
const getSuggestionsObject = (
  suggestedArray: SuggestedEvidenceRecord.AsObject[],
): SuggestionsObject =>
  suggestedArray.reduce((acc: SuggestionsObject, suggestion) => {
    const frameworkModelId =
      FRAMEWORK_MODEL_ID_TO_NAME[
        suggestion.complianceFrameworkBaseModelId as FRAMEWORK_MODEL_IDS
      ]

    acc[frameworkModelId] = suggestion.body
    return acc
  }, {})

export const getDedupedSuggestionsObject = (
  suggestedArray: SuggestedEvidenceRecord.AsObject[] | undefined,
): SuggestionsObject => {
  if (!Array.isArray(suggestedArray)) {
    return {}
  }
  const suggestions = getSuggestionsObject(suggestedArray)
  return dedupeSuggestions(suggestions)
}

/**
 * This function takes in an object of suggestions and returns in formatted markdown with duplicates handled
 * @param suggestions - An object of suggestions
 * @returns markdown - A markdown string
 */
export const getFormattedSuggestions = (
  suggestedArray: SuggestedEvidenceRecord.AsObject[] | undefined,
): string => {
  if (!Array.isArray(suggestedArray)) {
    return ''
  }
  const suggestions = getDedupedSuggestionsObject(suggestedArray)
  return convertSuggestionsToMarkdown(suggestions)
}

/**
 * This function takes in an object of suggestions and returns an array of objects with the framework name and the how to handle it
 * @param suggestions - An object of suggestions
 * @returns howToHandleItArray - An array of objects with the framework name and the how to handle it
 */

export const getFormattedSuggestionsForHowToHandleIt = (
  suggestedArray: HowToHandleItRecord.AsObject[] | undefined,
): HowToHandleItObject[] => {
  if (!Array.isArray(suggestedArray)) {
    return []
  }
  const suggestionsObject = getSuggestionsObject(suggestedArray)
  const dedupedSuggestions: SuggestionsObject =
    dedupeSuggestions(suggestionsObject)

  return Object.keys(dedupedSuggestions).reduce(
    (acc: HowToHandleItObject[], key: string) => {
      if (dedupedSuggestions[key]) {
        acc.push({
          frameworkName: key,
          howToHandleIt: dedupedSuggestions[key],
        })
      }
      return acc
    },
    [],
  )
}

/**
 * This function takes determines if the old how to handle it should be shown
 * WE NEED THIS TO INSURE BACKWARDS COMPATIBILITY WITH THE OLD HOW TO HANDLE IT 2/27/2023
 * @param suggestedArray - An array of suggestions
 * @param isLoading - A boolean that determines if the suggestions are loading
 * @param oldHowToHandleIt - A string of the old how to handle it
 * @returns shouldShowOldHowToHandleIt - A boolean that determines if the old how to handle it should be shown
 */

export const shouldShowOldHowToHandleIt = (
  suggestedArray: HowToHandleItRecord[],
  isLoading: boolean,
  oldHowToHandleIt: string,
): boolean => {
  if (isLoading) {
    return false
  } else if (suggestedArray.length) {
    return false
  } else if (!oldHowToHandleIt.length) {
    return false
  } else {
    return true
  }
}
